import React, {
  useCallback, useEffect, useRef, useState,
} from "react";

import moment from "moment";

import CustomCalendar from "./CustomCalendar";
import WeekMonthCheckbox from "./WeekMonthCheckbox";

import "./MonthFrequencyForm.css";

export default function MonthFrequencyForm({
  allowMultiple = false,
  updateSamplingData,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  dayOfMonth,
  setDayOfMonth,
  layout = "env-scheduling", // value can be "env-scheduling" or "supply-request-recurrence"
  minStartDate,
  allowPastStartDate = false,
  apiDates,
}) {
  const [recurrences, setRecurrences] = useState(0);
  const useApiDatesRef = useRef(Boolean(apiDates?.length));

  useEffect(() => {
    useApiDatesRef.current = Boolean(apiDates?.length);
  }, [apiDates]);

  const markUserChanged = () => {
    useApiDatesRef.current = false;
  };

  const flatPickerOptions = {
    plugins: [],
    dateFormat: "m/d/Y",
    mode: "single",
    minDate: minStartDate ?? moment().format("MM/DD/YYYY"),
    // maxDate: moment().add(1, "year").format("MM/DD/YYYY"),
  };

  /**
   * Find no. of recurrences
   */
  const getRecurrences = useCallback(() => {
    const start = moment(startDate, "MM/DD/YYYY");
    const end = moment(endDate, "MM/DD/YYYY");
    if (!end.isAfter(start, "day")) {
      return { recurrences: 0, dates: [] };
    }
    let count = 0;
    const dates = [];
    const currentDate = start.clone();
    while (currentDate.isSameOrBefore(end, "day")) {
      if (dayOfMonth.includes(currentDate.date().toString().toLowerCase())) {
        dates.push(currentDate.format("MM/DD/YYYY"));
        count++;
      }
      currentDate.add(1, "day");
    }
    return { recurrences: count, dates };
  }, [endDate, startDate, dayOfMonth]);

  const supplyEndPickerMinDate = moment(startDate, "MM/DD/YYYY").add(1, "day").format("MM/DD/YYYY");
  const showRecurrenceError = recurrences === 0;

  useEffect(() => {
    if (useApiDatesRef.current && apiDates?.length > 0) {
      setRecurrences(apiDates.length);
      return;
    }
    const { recurrences: recur, dates } = getRecurrences();
    setRecurrences(recur);
    updateSamplingData(dates, "monthly");
  }, [getRecurrences, apiDates]); // eslint-disable-line

  /**
   * Handles change of month
   * @param {string[]} value Array of selected month dates
   */
  const handleChange = (value) => {
    markUserChanged();
    setDayOfMonth(value);
  };

  if (layout === "supply-request-recurrence") {
    return (
      <div className="FrequencyForm__Content MonthFrequencyForm SupplyRequest">
        <div className="Calendar__GridContainer">

          <div className="Calendar__Grid">
            <span className="FieldTitle">Start Day of repeat</span>
            <WeekMonthCheckbox
              type="month"
              allowMultiple={allowMultiple}
              onChange={handleChange}
              containerStyle="MonthFrequencyForm__MonthGrid"
              selectedDay={dayOfMonth}
            />
          </div>
        </div>
        <div>
          <div className="StartDateSelection">
            <div className="Field No_Border">
              <span className="FieldTitle ExtraWidth">Start Date</span>
              <CustomCalendar
                value={startDate}
                allowPastDates={allowPastStartDate}
                onChange={([date]) => {
                  markUserChanged();
                  const momentDate = moment(date);
                  const formattedDate = momentDate.format("MM/DD/YYYY");
                  setStartDate(formattedDate);
                  if (!endDate || !moment(endDate, "MM/DD/YYYY").isAfter(momentDate, "day")) {
                    setEndDate(momentDate.clone().add(1, "day").format("MM/DD/YYYY"));
                  }
                }}
                options={{ mode: "single", dateFormat: "m/d/Y" }}
              />
            </div>
            <div className="Field">
              <span className="FieldTitle ExtraWidth">End Date</span>
              <CustomCalendar
                value={endDate}
                allowPastDates={allowPastStartDate}
                onChange={([date]) => {
                  markUserChanged();
                  const momentDate = moment(date);
                  if (!momentDate.isAfter(moment(startDate, "MM/DD/YYYY"), "day")) return;
                  setEndDate(momentDate.format("MM/DD/YYYY"));
                }}
                options={{
                  mode: "single",
                  dateFormat: "m/d/Y",
                  minDate: supplyEndPickerMinDate,
                }}
              />
            </div>
          </div>
          <div className="Field">
            <span className="FieldTitle MonthFrequencyForm__RecurrenceText">No of recurrences</span>
            <div className="Recurrences">
              {recurrences}
            </div>
            {showRecurrenceError && (
              <span className="SupplyRequestRecurrenceForm__Error">
                At Least One recurrence is required for the selected date
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="MonthFrequencyForm">
      <div className="Calendar__Grid">
        <span className="FieldTitle">Start Day of repeat</span>
        <WeekMonthCheckbox
          type="month"
          allowMultiple={allowMultiple}
          onChange={handleChange}
          containerStyle="MonthFrequencyForm__MonthGrid"
          selectedDay={dayOfMonth}
        />
      </div>
      <div>
        <div className="Field No_Border">
          <span className="FieldTitle ExtraWidth">Start Date</span>
          <CustomCalendar
            value={startDate}
            onChange={([date]) => {
              setStartDate(date);
              if (endDate && moment(endDate, "MM/DD/YYYY").isBefore(moment(date, "MM/DD/YYYY"), "day")) {
                setEndDate(date);
              }
            }}
            options={flatPickerOptions}
          />
        </div>
        <div className="Field">
          <span className="FieldTitle ExtraWidth">End Date</span>
          <CustomCalendar
            value={endDate}
            onChange={([date]) => {
              if (startDate && moment(startDate, "MM/DD/YYYY").isAfter(moment(date, "MM/DD/YYYY"), "day")) return;
              setEndDate(date);
            }}
            options={{
              ...flatPickerOptions,
              minDate: startDate,
            }}
          />
        </div>
        <div className="Field">
          <span className="FieldTitle MonthFrequencyForm__RecurrenceText">No of recurrences</span>
          <div className="Recurrences">
            {recurrences}
          </div>
        </div>
      </div>
    </div>
  );
}
